import { TitleCasePipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NotificationService } from '../../main/notification.service';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { PropertyComponent } from '../../main/property/property.component';
import { ChargerSettingsChargeSchedule, DayKey, SettingWithMetadata } from '../../models';
import { ChargerSettingsService } from '../charger-settings.service';
import { ChargersService } from '../chargers.service';


@Component({
  selector: 'app-charger-settings-chargeschedule',
  imports: [MatListModule, MatIconModule, MatSelectModule, MatOptionModule, MatInputModule, MatSlideToggleModule, PropertyComponent, MatProgressSpinnerModule, MatGridListModule, TitleCasePipe, MatCardModule],
  templateUrl: './charger-settings-chargeschedule.component.html',
  styleUrl: './charger-settings-chargeschedule.component.scss'
})
export class ChargerSettingsChargescheduleComponent implements OnInit {
	chargerId!: string;
	chargerSettings = signal<ChargerSettingsChargeSchedule | null>(null);
	chargerSettingsLoaded = signal<boolean>(false);

	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private chargersService = inject(ChargersService);
	private chargerSettingsService = inject(ChargerSettingsService);
	private notificationService = inject(NotificationService);
	private destroyRef = inject(DestroyRef);

	readonly days: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

	ngOnInit() {
		this.chargerId = this.route.parent?.snapshot.paramMap.get('id') ?? '';
		const isValid = /^[PD]\d{14}$/.test(this.chargerId);
		if (!isValid) {
			this.router.navigate(['/404']);
		}

		this.chargersService.getChargerSettingsChargeSchedule(this.chargerId).pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: (charger) => {
				console.log('getChargerSettingsChargeSchedule():', charger);

				this.chargerSettings.set(charger);
				this.chargerSettingsLoaded.set(true);
			},
			error: (err) => {
				console.error('getChargerSettingsChargeSchedule() error:', err);
				this.notificationService.openSnackbar(false, err.error.errorMessage, $localize`:@@Load:Load`);
				this.chargerSettingsLoaded.set(true);
			},
		});
	}


	saveCallback = (setting: SettingWithMetadata) => this.saveSetting(setting); // for preserving `this` context in `saveSetting`

	saveSetting(setting: SettingWithMetadata): Observable<boolean> {
		console.log('saveSetting:', setting);
		let value;
		switch (setting.type) {
			case 'DelayedStart':
			{
				let valueWithMetadata = this.chargerSettingsService.returnDelayedStartWithMetaData(setting.valueWithMetadata);
				if (valueWithMetadata) {
					value = {
						beginTimeHour: valueWithMetadata.value.beginTimeHour.value,
						beginTimeMinute: valueWithMetadata.value.beginTimeMinute.value,
						chargingMode: valueWithMetadata.value.chargingMode.value,
					}
				}
				break;
			}
			case 'DaySchedule':
			{
				let valueWithMetadata = this.chargerSettingsService.returnDaySchedule(setting.valueWithMetadata)
				if (valueWithMetadata) {
					let day = setting.id.split('.')[1] as DayKey;
					let weekSchedule = {
						monday: this.chargerSettings()?.weekSchedule.monday,
						tuesday: this.chargerSettings()?.weekSchedule.tuesday,
						wednesday: this.chargerSettings()?.weekSchedule.wednesday,
						thursday: this.chargerSettings()?.weekSchedule.thursday,
						friday: this.chargerSettings()?.weekSchedule.friday,
						saturday: this.chargerSettings()?.weekSchedule.saturday,
						sunday: this.chargerSettings()?.weekSchedule.sunday,
					}
					weekSchedule[day] = valueWithMetadata;
					value = weekSchedule;
					setting.id = 'weekSchedule';
				}
				break;
			}
			default:
				if ((setting.valueWithMetadata) && ('value' in setting.valueWithMetadata)) {
					value = setting.valueWithMetadata?.value;
				}
				break;
		}

		if (setting && value !== null) {
			return this.chargersService.putChargerSettingsChargeSchedule(this.chargerId, setting.id, value).pipe(
				map(result => {
					return this.notificationService.openSnackbar(result.success, result.errorMessage);
				}),
				catchError(err => {
					return of(this.notificationService.openSnackbar(false, err.message));
				})			
			);
		}
		console.log('saveSetting() skipped — setting or value invalid:', setting, '=', value);
		return of(false);
	}

}
